/**
 * Native binding interface for Settings module
 * This module provides communication with native platform for settings management
 * Updated to use string parameters with descriptive names for JSON serialization
 *
 * Flow Direction:
 * - Native → Web: initialize, onGetSettingComplete, onGetSettingsComplete, onSetSettingComplete, onSetSettingsComplete
 * - Web → Native: getSetting, getSettings, setSetting, setSettings, onInitializeComplete, writeTrace, settingsReady
 */
declare const settingsNative: {
	// Web → Native calls (web calls these to send requests to native)
	getSetting(id: string, getSettingParams: string): void;
	getSettings(id: string, getSettingsParams: string): void;
	setSetting(id: string, setSettingParams: string): void;
	setSettings(id: string, setSettingsParams: string): void;
	onInitializeComplete(id: string, response: string): void;
	writeTrace(writeTraceParams: string): void;
	settingsReady(): void;

	// Native → Web callbacks (native calls these to send responses/events to web)
	initialize(initializeParams: string): void;
	refreshSetting(settingKey: string): void;
	onGetSettingComplete(id: string, response: string): void;
	onGetSettingsComplete(id: string, response: string): void;
	onSetSettingComplete(id: string, response: string): void;
	onSetSettingsComplete(id: string, response: string): void;
};

// Conditional export that checks if settingsNative is available
// This prevents runtime errors when the dummy bridge hasn't loaded yet
const getSettingsNative = (): typeof settingsNative | null => {
	if (typeof window !== 'undefined' && typeof window.settingsNative !== 'undefined') {
		return window.settingsNative as typeof settingsNative;
	}
	throw new Error(
		'settingsNative is not available - ensure the native bridge is properly loaded'
	);
};

// Export the function instead of calling it immediately
export default getSettingsNative;
